import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import loadable from '@loadable/component';
import { plainTextStyle } from '@westwing/ui-kit/typography';
import { forDesktop, forSemiWideScreens, maxedPageWrapperStyle } from '@westwing/ui-kit/breakPoints';
import HybridLink from '../../Generic/Links/HybridLink';
import useFeatureToggle from '../../../hooks/useFeatureToggle';
import useBellaTranslator, { BellaTranslator } from '../../../hooks/useTranslator';
import useConfig from '../../../hooks/useConfig';
import { getNewProductsUrl, getCategoriesUrl } from '../../../urlService';
import { Config } from '../../../../common/types/config';
import { FeatureToggles } from '../../../../common/types/featureToggles';
import { CategoryData } from '../../../redux/category/types';
import StateInterface from '../../../redux/types';
import { showOverlay, hideOverlay } from '../../../redux/overlays/actions';
import { OverlayId } from '../../Overlays/types';
import { pagePadding } from '../../Generic/BoxHelpers';
import useGa from '../../../hooks/useGa';
import useRouter from '../../../hooks/useRouter';
import { VictorQaClasses } from '../../../../common/qaIds';
import { TrackHeaderActions } from '../../../../common/ga/types';
import { useCurrentPageType } from '../../../hooks/UrlManager/pageTypeHooks';

const NavigationFlyout = loadable(() => import('./Flyout/NavigationFlyout'));
const CategoryFlyoutContent = loadable(() => import('./Flyout/CategoryFlyoutContent'));
const LooksFlyoutContent = loadable(() => import('./Flyout/LooksFlyoutContent'));

const proactivelyFetch = () => {
    /* eslint-disable no-unused-expressions */
    import('./Flyout/NavigationFlyout');
    import('./Flyout/CategoryFlyoutContent');
    import('./Flyout/LooksFlyoutContent');
    /* eslint-enable no-unused-expressions */
};

const StyledNavWrapper = styled.div`
    display: none;
    ${forDesktop`
        display: block;
    `}
`;

const StyledNav = styled.nav`
    display: block;
    border: 1px solid ${({ theme }) => theme.colors.charcoal20};
    border-style: solid none;
`;

const StyledNavItemsList = styled.ul`
    display: flex;
    flex-direction: row;
    overflow: hidden;
    justify-content: space-between;
    align-items: center;
    ${pagePadding}
    ${maxedPageWrapperStyle}
`;

const StyledNavLink = styled(HybridLink)<{ hexColor?: string }>`
    ${plainTextStyle}
    font-family: ${({ theme }) => theme.fontBrandonMedium};
    color: ${({ theme }) => theme.colors.charcoal80};
    /* repeated color property to make sure we fallback to the style guide color , when category hex color is not valid*/
    color: ${({ hexColor }) => hexColor || ''};
`;

const StyledNavLinkText = styled.span<{ hasShortName: boolean }>`
    white-space: nowrap;
    ${({ hasShortName }) =>
        hasShortName
            ? forSemiWideScreens`
        display: none;
    `
            : ''}
`;

const StyledNavLinkLongText = styled.span<{ hasShortName: boolean }>`
    display: ${({ hasShortName }) => (!hasShortName ? 'block' : 'none')};
    ${forSemiWideScreens`
        display: inline-block;
    `}
`;

const StyledNavItem = styled.li`
    padding: 13.5px 0;
    position: relative;
    :hover > div {
        display: block;
    }
`;

const Hover = styled.div`
    display: none;
    width: 100%;
    height: 1px;
    background: ${({ theme }) => theme.colors.charcoal100};
    position: absolute;
    bottom: 0.5px;
`;

enum FlyoutKind {
    Category,
    Looks,
    None,
}

interface TopLvlNav extends CategoryData {
    kind: FlyoutKind;
    classNames?: {
        long: string;
        short: string;
    };
}

const CHARCOAL100 = '#323232';

const createFirstLevelItems = (t: BellaTranslator, config: Config): TopLvlNav[] => [
    {
        name: t('NEW'),
        url: getNewProductsUrl(),
        isStaticCategory: true,
        kind: FlyoutKind.None,
        depthLevel: 1,
        hexColor: CHARCOAL100,
    },
    {
        name: t('Westwing_Collection_Navigation_Long'),
        url: config.links.westwingCollection,
        shortName: t('Westwing_Collection_Navigation_Short'),
        isStaticCategory: true,
        kind: FlyoutKind.None,
        depthLevel: 1,
        hexColor: CHARCOAL100,
    },
    {
        name: t('Shop the Look'),
        url: config.links.looks,
        shortName: t('Looks'),
        isStaticCategory: true,
        kind: FlyoutKind.Looks,
        depthLevel: 1,
        classNames: {
            long: VictorQaClasses.looksNavItemLong,
            short: VictorQaClasses.looksNavItemShort,
        },
    },
];

const createLastLevelItems = (t: BellaTranslator, config: Config, featureToggles: FeatureToggles): TopLvlNav[] => {
    const items: TopLvlNav[] = [];
    if (featureToggles.interiorServiceInNav) {
        items.push({
            name: t('Interior Design Service'),
            url: config.links.interiorService,
            shortName: t('Interior_Design_Navigation_Short'),
            isStaticCategory: true,
            kind: FlyoutKind.None,
            depthLevel: 1,
        });
    }

    return items;
};

const Flyouts: React.FC<{ currentFlyoutKind: FlyoutKind; activeCategory: CategoryData }> = ({
    currentFlyoutKind,
    activeCategory,
}) => {
    return (
        <NavigationFlyout>
            {currentFlyoutKind === FlyoutKind.Category && (
                <CategoryFlyoutContent
                    flyoutTeaserData={activeCategory.flyoutData}
                    activeCategoryName={activeCategory.name}
                    subMenuNavItems={activeCategory.children || []}
                />
            )}
            {currentFlyoutKind === FlyoutKind.Looks && <LooksFlyoutContent />}
        </NavigationFlyout>
    );
};

const catToTopLvl = (cat: CategoryData): TopLvlNav => ({
    ...cat,
    kind: FlyoutKind.Category,
});

const Navigation = () => {
    const [useFlyout, setUseFlyout] = useState<Boolean>(false);
    const config = useConfig();
    const translate = useBellaTranslator();
    const featureToggles = useFeatureToggle();
    const { pageType } = useCurrentPageType();
    const gaTracking = useGa();
    const { host } = useRouter();
    const visibleCategoryData = useSelector<StateInterface, CategoryData[]>(
        state => state.categories.visibleOriginalCategories
    );
    const dispatch = useDispatch();

    const navItems: TopLvlNav[] = [
        ...createFirstLevelItems(translate, config),
        ...visibleCategoryData.map(catToTopLvl),
        ...createLastLevelItems(translate, config, featureToggles),
    ];

    const [currentTopLvlNavItem, setCurrentTopLvlNavItem] = useState<TopLvlNav>(navItems[0]);
    const [hoverTimeOut, setHoverTimeOut] = useState<ReturnType<typeof setTimeout>>();

    const dismissCategoryFlyout = () => {
        window.clearTimeout(hoverTimeOut);
        dispatch(hideOverlay(OverlayId.DESKTOP_NAV_FLYOUT));
    };

    const trackEcommerceForTopLevelCategory = (navItem: TopLvlNav) => {
        if (navItem.kind === FlyoutKind.Category) {
            gaTracking.oneHeader.trackFirstCategoryPromoView({
                name: navItem.name,
                destinationUrl: getCategoriesUrl(navItem.url, host),
                creative: undefined,
            });
            return;
        }
        gaTracking.oneHeader.trackFirstCategoryPromoView();
    };

    const setActiveSubMenu = (navItem: TopLvlNav) => {
        proactivelyFetch();
        if (navItem.kind !== FlyoutKind.None) {
            setUseFlyout(() => true);
            setCurrentTopLvlNavItem(() => navItem);
            dispatch(showOverlay(OverlayId.DESKTOP_NAV_FLYOUT));
            dispatch(hideOverlay(OverlayId.DESKTOP_CART_OVERLAY));
        } else {
            dismissCategoryFlyout();
        }
        trackEcommerceForTopLevelCategory(navItem);
    };

    const handleNavItemHover = (navItem: TopLvlNav) => {
        const timeoutID: ReturnType<typeof setTimeout> = window.setTimeout(() => setActiveSubMenu(navItem), 100);
        setHoverTimeOut(timeoutID);
    };

    const handleTrackingActions = (navItem: TopLvlNav) => {
        gaTracking.oneHeader.trackHeaderAction(
            'first-level-category-click',
            TrackHeaderActions.HeaderFirstLevelNavClick,
            navItem.name
        );

        if (navItem.url === config.links.interiorService) {
            gaTracking.oneHeader.trackHeaderAction(
                'headernavbarinteriordesignclick',
                TrackHeaderActions.HeaderInteriorDesignClick,
                pageType
            );
        }
    };

    return (
        <StyledNavWrapper onMouseLeave={dismissCategoryFlyout}>
            <StyledNav>
                <StyledNavItemsList>
                    {navItems.map(topLvlNavItem => {
                        const { name, url, shortName, isStaticCategory, hexColor, classNames } = topLvlNavItem;
                        return (
                            <StyledNavItem
                                key={name}
                                onMouseEnter={() => handleNavItemHover(topLvlNavItem)}
                                onClick={() => handleTrackingActions(topLvlNavItem)}
                                className={VictorQaClasses.headerNavItem}
                            >
                                <StyledNavLink to={!isStaticCategory ? getCategoriesUrl(url) : url} hexColor={hexColor}>
                                    <StyledNavLinkText
                                        className={classNames && classNames.short}
                                        hasShortName={Boolean(shortName)}
                                    >
                                        {shortName}
                                    </StyledNavLinkText>
                                    <StyledNavLinkLongText
                                        className={classNames && classNames.long}
                                        hasShortName={Boolean(shortName)}
                                    >
                                        {name}
                                    </StyledNavLinkLongText>
                                </StyledNavLink>
                                <Hover />
                            </StyledNavItem>
                        );
                    })}
                </StyledNavItemsList>
            </StyledNav>
            {useFlyout && (
                <Flyouts currentFlyoutKind={currentTopLvlNavItem.kind} activeCategory={currentTopLvlNavItem} />
            )}
        </StyledNavWrapper>
    );
};

export default Navigation;
